import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { readError } from '../lib/errors'
import { COLORS_BY_ID, type ColorId } from '../games/colorful/data/colors'
import { CATEGORIES } from '../games/colorful/data/categories'
import {
  fetchColorfulPicks, fetchColorfulPlayers, fetchColorfulRoomByCode,
} from '../games/colorful/api/rooms'
import { RoomQR } from '../components/RoomQR'
import { TvJoinBadge } from './TvJoinBadge'
import type {
  ColorfulPickRow, ColorfulPlayerRow, ColorfulRoomRow,
} from '../lib/database.types'
import { useT } from '../i18n'

interface Props { code: string }

/**
 * Colorful TV room display — strictly read-only. The TV never has host
 * controls; whoever created the room drives everything from their phone
 * via the main app's ColorfulRoomScreen. The TV just subscribes to
 * realtime and renders the current phase as a big-screen display.
 */
export function ColorfulTvRoomScreen({ code }: Props) {
  const t = useT()
  const [room, setRoom] = useState<ColorfulRoomRow | null>(null)
  const [players, setPlayers] = useState<ColorfulPlayerRow[]>([])
  const [picks, setPicks] = useState<ColorfulPickRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  // ── Load + subscribe ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const r = await fetchColorfulRoomByCode(code)
        if (cancelled) return
        if (!r) { setNotFound(true); setLoading(false); return }
        setRoom(r)
        const [ps, pk] = await Promise.all([
          fetchColorfulPlayers(r.id),
          fetchColorfulPicks(r.id),
        ])
        if (cancelled) return
        setPlayers(ps); setPicks(pk)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [code])

  useEffect(() => {
    if (!room) return
    const roomId = room.id
    const channel = supabase.channel(`colorful-tv-${roomId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'colorful_rooms', filter: `id=eq.${roomId}` },
        (payload) => { if (payload.eventType !== 'DELETE') setRoom(payload.new as ColorfulRoomRow) })
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'colorful_players', filter: `room_id=eq.${roomId}` },
        () => { fetchColorfulPlayers(roomId).then(setPlayers).catch(() => {}) })
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'colorful_picks', filter: `room_id=eq.${roomId}` },
        () => { fetchColorfulPicks(roomId).then(setPicks).catch(() => {}) })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [room?.id])

  // ── Gates ─────────────────────────────────────────────────────────────
  if (notFound) return <FullMessage title={t('Room not found')} body={t('No Colorful game is running under code {code}.', { code })} />
  if (error)    return <FullMessage title={t('Error')} body={error} />
  if (loading || !room) return <FullMessage title={t('Loading room…')} body={t('Code {code}', { code })} />

  // ── Phase dispatch — all read-only. The join badge rides along on
  // every phase so the code, QR, and address never leave the screen. ──
  const view =
    room.phase === 'lobby'       ? <TvLobby      room={room} players={players} /> :
    room.phase === 'setup'       ? <TvSetup      room={room} /> :
    room.phase === 'round_intro' ? <TvRoundIntro room={room} players={players} /> :
    room.phase === 'picker_draw' ? <TvPickerDraw room={room} players={players} /> :
    room.phase === 'picking'     ? <TvPicking    room={room} players={players} picks={picks} /> :
    room.phase === 'results'     ? <TvResults    room={room} players={players} picks={picks} /> :
    null

  return <>{view}<TvJoinBadge code={room.code} /></>
}

// ── Shells + subviews ─────────────────────────────────────────────────────

function Stage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-brand-bg
                    text-brand-text px-12 py-12 text-center">
      {children}
    </div>
  )
}

function FullMessage({ title, body }: { title: string; body: string }) {
  return (
    <Stage>
      <h1 className="font-display text-5xl font-bold mb-4">{title}</h1>
      <p className="text-2xl text-brand-muted">{body}</p>
    </Stage>
  )
}

function PlayerAvatar({
  player, size = 'md',
}: { player: ColorfulPlayerRow; size?: 'sm' | 'md' | 'lg' }) {
  const dim = { sm: 'w-12 h-12 text-2xl', md: 'w-20 h-20 text-4xl', lg: 'w-28 h-28 text-5xl' }[size]
  return (
    <div className={`${dim} rounded-full bg-brand-elevated flex items-center justify-center shrink-0`}>
      {player.avatar_emoji ?? (player.display_name[0] ?? '?').toUpperCase()}
    </div>
  )
}

// ── Lobby ──────────────────────────────────────────────────────────────────

function TvLobby({
  room, players,
}: { room: ColorfulRoomRow; players: ColorfulPlayerRow[] }) {
  const t = useT()
  const host = 'games.jordandraper.com'

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg text-brand-text">
      <header className="px-12 pt-10 pb-4 text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted mb-2">{t('Colorful · waiting for players')}</p>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-12">
        <p className="text-lg font-semibold uppercase tracking-[0.3em] text-brand-muted mb-4">{t('Room code')}</p>
        <p className="font-display font-black text-game-colorful-dark tracking-[0.25em] leading-none"
           style={{ fontSize: 'clamp(7rem, 18vw, 16rem)' }}>
          {room.code}
        </p>

        <p className="font-display text-2xl text-brand-text mt-10 max-w-3xl mx-auto text-center">
          {t('On your phone, go to')}{' '}
          <span className="font-mono font-bold">{host}/join</span>
          {' '}{t('and enter the code.')}
        </p>
        <p className="font-display text-lg text-brand-muted mt-2 max-w-2xl mx-auto text-center">{t('No account needed. Guests can play.')}</p>

        <div className="mt-8">
          <RoomQR code={room.code} size={208} />
        </div>

        <div className="mt-12 w-full max-w-5xl">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted mb-4">
            {t('Players · {n} / 6', { n: players.length })}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {players.map(p => (
              <div key={p.id} className="flex flex-col items-center gap-2">
                <PlayerAvatar player={p} size="lg" />
                <p className="font-display text-xl font-bold text-brand-text max-w-40 truncate">
                  {p.display_name}
                </p>
                {p.is_ai && (
                  <span className="text-[11px] font-semibold text-brand-muted
                                   bg-brand-elevated px-2 py-0.5 rounded-full
                                   border border-brand-border">AI</span>
                )}
              </div>
            ))}
            {players.length < 6 && (
              <div className="flex flex-col items-center gap-2 opacity-40">
                <div className="w-28 h-28 rounded-full border-2 border-dashed border-brand-border
                                flex items-center justify-center text-4xl text-brand-muted">+</div>
                <p className="font-display text-base text-brand-muted">{t('Waiting…')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="px-12 py-8 text-center text-brand-muted">{t('Host starts the game from their phone.')}</footer>
    </div>
  )
}

// ── Setup ────────────────────────────────────────────────────────────────

function TvSetup({ room }: { room: ColorfulRoomRow }) {
  const t = useT()
  return (
    <Stage>
      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted mb-4">{t("Tonight you won't be needing")}</p>
      <h1 className="font-display text-7xl font-bold text-brand-text mb-12">{t('These five colors')}</h1>
      <div className="flex flex-wrap justify-center gap-6 max-w-4xl">
        {room.discarded_colors.map(id => (
          <div key={id}
               className="relative w-32 h-32 rounded-2xl shadow-card-lg border border-black/5 opacity-70"
               style={{ backgroundColor: COLORS_BY_ID[id as ColorId].hex }}>
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden>
              <line x1="12" y1="12" x2="88" y2="88"
                    stroke="rgba(0,0,0,0.45)" strokeWidth="6" strokeLinecap="round" />
            </svg>
          </div>
        ))}
      </div>
      <p className="text-xl text-brand-muted mt-12 max-w-3xl">
        {t('The {n} remaining colors are shared by every player.', { n: room.available_colors.length })}
      </p>
    </Stage>
  )
}

// ── Round intro ──────────────────────────────────────────────────────────

function TvRoundIntro({
  room, players,
}: { room: ColorfulRoomRow; players: ColorfulPlayerRow[] }) {
  const t = useT()
  const picker = players[room.picker_index % Math.max(1, players.length)]
  return (
    <Stage>
      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted mb-2">
        {t('Round {n} of {total}', { n: room.current_round + 1, total: room.max_rounds })}
      </p>
      <div className="my-8">
        {picker && <PlayerAvatar player={picker} size="lg" />}
      </div>
      <h1 className="font-display text-7xl font-bold text-brand-text mb-2">
        {picker?.display_name ?? '…'}
      </h1>
      <p className="text-2xl text-brand-muted">
        {t('is drawing a card for this round.')}
      </p>
    </Stage>
  )
}

// ── Picker draw ──────────────────────────────────────────────────────────

function TvPickerDraw({
  room, players,
}: { room: ColorfulRoomRow; players: ColorfulPlayerRow[] }) {
  const t = useT()
  const card = CATEGORIES.find(c => c.id === room.pending_category_id)
  const slot = room.pending_slot ?? 0
  const categoryPrompt = card?.prompts[slot] ?? ''
  const picker = players[room.picker_index % Math.max(1, players.length)]

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg text-brand-text px-12 py-12">
      <header className="text-center mb-8">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted">
          {t('Round {n} of {total}', { n: room.current_round + 1, total: room.max_rounds })}
        </p>
        <h2 className="font-display text-4xl font-bold text-brand-text mt-1">
          {t('{name} is picking a subject', { name: picker?.display_name ?? '…' })}
        </h2>
      </header>

      <div className="flex-1 flex items-center justify-center">
        {card && (
          <div className="card-lg p-12 bg-linear-to-br from-brand-text to-gray-800 text-white
                          max-w-3xl w-full">
            <p className="text-lg uppercase tracking-[0.3em] text-white/60 mb-2">
              {t('Card #{n}', { n: card.id })}
            </p>
            <h3 className="font-display text-6xl font-black uppercase tracking-wide mb-8">
              {card.theme}
            </h3>
            <ul className="space-y-4">
              {card.prompts.map((p, i) => (
                <li key={i}
                    className={`flex items-center gap-6 rounded-xl px-6 py-4 transition-colors
                                ${i === slot
                                  ? 'bg-white text-brand-text font-bold'
                                  : 'text-white/50'}`}>
                  <span className="font-display font-black text-3xl w-14">
                    {i === 3 ? '4-5' : i + 1}
                  </span>
                  <span className="flex-1 font-display text-3xl">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <footer className="text-center mt-8 text-xl text-brand-muted">
        {t('Category:')} <span className="font-display text-brand-text font-bold">{categoryPrompt}</span>
      </footer>
    </div>
  )
}

// ── Picking ──────────────────────────────────────────────────────────────

function TvPicking({
  room, players, picks,
}: { room: ColorfulRoomRow; players: ColorfulPlayerRow[]; picks: ColorfulPickRow[] }) {
  const t = useT()
  const picked = new Set(
    picks.filter(pk => pk.round_index === room.current_round && pk.color_id).map(pk => pk.player_id),
  )
  const picker = players[room.picker_index % Math.max(1, players.length)]

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg text-brand-text px-12 py-12">
      <header className="text-center mb-8">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted">
          {t('Round {n} of {total}', { n: room.current_round + 1, total: room.max_rounds })}
          {' · '}
          {t('Subject chosen by {name}', { name: picker?.display_name ?? '…' })}
        </p>
      </header>

      <div className="flex-1 flex items-center justify-center">
        <div className="card-lg p-16 text-center bg-linear-to-br from-game-colorful-light to-white
                        border-4 border-game-colorful/40 max-w-5xl w-full">
          <p className="text-lg font-semibold uppercase tracking-[0.3em] text-brand-muted mb-4">{t('Tap the color you most associate with')}</p>
          <p className="font-display font-black text-brand-text leading-none"
             style={{ fontSize: 'clamp(5rem, 14vw, 12rem)' }}>
            {room.pending_subject}
          </p>
        </div>
      </div>

      <footer className="mt-8">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted mb-4 text-center">
          {t('{n} of {total} picked · no peeking until the finale', { n: picked.size, total: players.length })}
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          {players.map(p => (
            <div key={p.id}
                 className={`flex flex-col items-center gap-2 transition-opacity
                             ${picked.has(p.id) ? '' : 'opacity-40'}`}>
              <div className="relative">
                <PlayerAvatar player={p} size="md" />
                {picked.has(p.id) && (
                  <span className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full
                                   bg-iet-green text-white flex items-center justify-center
                                   text-lg font-bold border-4 border-brand-bg">✓</span>
                )}
              </div>
              <p className="font-display text-base font-semibold max-w-32 truncate">
                {p.display_name}
              </p>
            </div>
          ))}
        </div>
      </footer>
    </div>
  )
}

// ── Results (stepped final reveal) ───────────────────────────────────────

function TvResults({
  room, players, picks,
}: { room: ColorfulRoomRow; players: ColorfulPlayerRow[]; picks: ColorfulPickRow[] }) {
  const t = useT()
  const scored = useMemo(() => {
    type RoundPicks = Record<string, string>
    const rounds: RoundPicks[] = []
    for (let r = 0; r < room.max_rounds; r++) {
      const forRound: RoundPicks = {}
      for (const pk of picks) {
        if (pk.round_index === r && pk.color_id) forRound[pk.player_id] = pk.color_id
      }
      rounds.push(forRound)
    }
    const points: Record<string, number> = {}
    for (const p of players) points[p.id] = 0
    let coopWin = rounds.length > 0
    const perRound = rounds.map(rp => {
      const groups: Record<string, string[]> = {}
      for (const [pid, color] of Object.entries(rp)) (groups[color] ??= []).push(pid)
      const sizes = Object.values(groups).map(g => g.length)
      const total = Object.values(rp).length
      const max = sizes.length ? Math.max(...sizes) : 0
      const winning = Object.entries(groups).filter(([, g]) => g.length === max && max > 0).map(([c]) => c)
      const unanimous = total > 0 && max === total
      if (!unanimous) coopWin = false
      // V2 rulebook: ties for the majority all score 1 point each.
      for (const winningColor of winning) {
        for (const pid of groups[winningColor]) points[pid] = (points[pid] ?? 0) + 1
      }
      return {
        winningColor: winning.length === 1 ? winning[0] : null,
        groups,
        unanimous,
        tiedWinners: winning.length > 1 ? winning : null,
      }
    })
    const top = Math.max(0, ...Object.values(points))
    const winners = Object.entries(points).filter(([, p]) => p === top && top > 0).map(([pid]) => pid)
    return { points, perRound, coopWin, winners }
  }, [picks, players, room.max_rounds])

  // The reveal step lives on the room — the host advances it from their
  // phone and the TV just follows. (reveal_index isn't in the generated
  // types yet.)
  const step = (room as { reveal_index?: number }).reveal_index ?? 0
  const isFinal = step >= room.max_rounds

  function roundMeta(i: number) {
    const any = picks.find(pk => pk.round_index === i)
    const cat = any ? CATEGORIES.find(c => c.id === any.category_id) : null
    return {
      theme: cat?.theme ?? '…',
      subject: any?.subject ?? '…',
    }
  }

  if (!isFinal) {
    const meta = roundMeta(step)
    const rs = scored.perRound[step]
    const groups = Object.entries(rs.groups).sort((a, b) => b[1].length - a[1].length)
    const isLastRound = step + 1 >= room.max_rounds

    return (
      <div className="min-h-screen flex flex-col bg-brand-bg text-brand-text px-12 py-12">
        <header className="text-center mb-6">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted">
            {t('Reveal · Round {n} of {total} · {theme}', { n: step + 1, total: room.max_rounds, theme: meta.theme })}
          </p>
          <h1 className="font-display font-black text-brand-text mt-2 leading-none"
              style={{ fontSize: 'clamp(4rem, 10vw, 9rem)' }}>
            {meta.subject}
          </h1>
          {rs.unanimous && (
            <p className="text-2xl text-iet-green font-semibold mt-3">
              {t('✨ Unanimous!')}
            </p>
          )}
        </header>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-6xl flex flex-wrap justify-center gap-6">
            {groups.map(([color, playerIds]) => {
              const isMajority = color === rs.winningColor
              return (
                <div key={color}
                     className={`flex flex-col items-center gap-3 rounded-3xl p-6
                                 ${isMajority ? 'bg-brand-gold/10 border-4 border-brand-gold/40 shadow-card-lg' : ''}`}>
                  <div className="w-32 h-32 rounded-2xl shadow-card-lg border border-black/5"
                       style={{ backgroundColor: COLORS_BY_ID[color as ColorId].hex }} />
                  <p className="font-display text-2xl font-bold">
                    {COLORS_BY_ID[color as ColorId].name}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 max-w-[16rem]">
                    {playerIds.map(pid => {
                      const p = players.find(x => x.id === pid)
                      return p ? (
                        <span key={pid} className="flex items-center gap-1.5 px-3 py-1 rounded-full
                                                   bg-brand-elevated text-sm">
                          <span>{p.avatar_emoji ?? (p.display_name[0] ?? '?').toUpperCase()}</span>
                          <span className="font-medium">{p.display_name}</span>
                        </span>
                      ) : null
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <footer className="text-center mt-8">
          <p className="text-2xl text-brand-muted">
            {isLastRound ? t('Final scores next…') : t('Host advances the reveal on their phone →')}
          </p>
        </footer>
      </div>
    )
  }

  // ── Final scoreboard ──────────────────────────────────────────────────
  const sorted = [...players].sort((a, b) => (scored.points[b.id] ?? 0) - (scored.points[a.id] ?? 0))

  // The whole match beside the standings: every round's subject and
  // who picked what color — the story, not just the score.
  const recap = scored.perRound
    .map((r, i) => ({
      index: i,
      ...roundMeta(i),
      unanimous: r.unanimous,
      groups: Object.entries(r.groups)
        .sort((a, b) => b[1].length - a[1].length)
        .map(([color, ids]) => ({
          color: color as ColorId,
          names: ids.map(id => players.find(p => p.id === id)?.display_name).filter(Boolean) as string[],
          isMajority: color === r.winningColor,
        })),
    }))
    .filter(r => r.groups.length > 0)

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg text-brand-text px-12 py-12">
      <header className="text-center mb-8">
        {scored.coopWin ? (
          <>
            <div className="text-8xl mb-3">🌈</div>
            <p className="text-sm font-semibold text-game-colorful uppercase tracking-[0.3em] mb-3">{t('Co-op Victory')}</p>
            <h1 className="font-display text-6xl font-black text-brand-text">{t('You all saw the same colors.')}</h1>
          </>
        ) : scored.winners.length === 0 ? (
          <>
            <div className="text-8xl mb-3">🎭</div>
            <h1 className="font-display text-6xl font-black text-brand-text">{t('No winners this game')}</h1>
            <p className="text-xl text-brand-muted mt-2">{t('Every category ended in a tie. Beautifully un-aligned.')}</p>
          </>
        ) : (
          <>
            <div className="text-8xl mb-3">🏆</div>
            <p className="text-sm font-semibold text-brand-gold uppercase tracking-[0.3em] mb-3">
              {scored.winners.length > 1 ? t('Shared victory') : t('Winner')}
            </p>
            <h1 className="font-display font-black text-brand-text leading-none"
                style={{ fontSize: 'clamp(3.5rem, 9vw, 7.5rem)' }}>
              {scored.winners.map(id => players.find(p => p.id === id)?.display_name).filter(Boolean).join(' & ')}
            </h1>
            <p className="text-2xl text-brand-primary font-bold mt-4">
              {scored.points[scored.winners[0]] === 1
                ? t('1 point')
                : t('{n} points', { n: scored.points[scored.winners[0]] })}
            </p>
          </>
        )}
      </header>

      <div className="flex-1 flex items-start justify-center gap-10 overflow-y-auto">
        <div className="w-full max-w-xl space-y-3 shrink-0">
          {sorted.map((p, i) => (
            <div key={p.id}
                 className="flex items-center gap-6 px-8 py-4 rounded-2xl bg-white shadow-card">
              <span className="font-display text-3xl font-black text-brand-muted w-12 text-center">
                {i + 1}
              </span>
              <PlayerAvatar player={p} size="md" />
              <span className="flex-1 font-display text-3xl font-bold">{p.display_name}</span>
              <span className="font-display text-4xl font-black text-brand-text">
                {scored.points[p.id] ?? 0}
              </span>
            </div>
          ))}
        </div>

        {recap.length > 0 && (
          <div className="flex-1 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-muted mb-4">
              {t('Every round')}
            </p>
            <div className="grid grid-cols-2 gap-4">
              {recap.map(r => (
                <div key={r.index} className="rounded-2xl bg-white shadow-card p-5">
                  <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted">
                    {t('Round {n}', { n: r.index + 1 })} · {r.theme}
                  </p>
                  <p className="font-display text-2xl font-bold leading-tight mt-1">{r.subject}</p>
                  {r.unanimous && (
                    <p className="text-sm text-iet-green font-semibold mt-1">{t('✨ Unanimous!')}</p>
                  )}
                  <div className="mt-3 space-y-1.5">
                    {r.groups.map(g => (
                      <div key={g.color}
                           className={`flex items-center gap-3 rounded-xl px-2 py-1
                                       ${g.isMajority ? 'bg-brand-gold/10 border border-brand-gold/40' : ''}`}>
                        <span className="w-6 h-8 rounded-md border border-black/10 shrink-0"
                              style={{ backgroundColor: COLORS_BY_ID[g.color]?.hex }} />
                        <p className="flex-1 min-w-0 text-base leading-snug">
                          <span className="font-semibold">{COLORS_BY_ID[g.color]?.name}</span>{' '}
                          <span className="text-brand-muted">{g.names.join(', ')}</span>
                        </p>
                        <span className="font-display font-bold shrink-0">×{g.names.length}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <footer className="text-center mt-8">
        <a
          href="/tv/colorful"
          className="inline-block px-12 py-6 rounded-3xl bg-brand-primary text-white
                     font-display text-2xl font-bold shadow-card-lg active:scale-95 transition-transform"
        >{t('Play again')}</a>
      </footer>
    </div>
  )
}
